import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtEUR, fmtQty } from "@/lib/format";
import { Loading, btnGhost, btnPrimary } from "@/components/app/common";

const Cost = ({ v }) => (v == null ? "—" : fmtEUR(v));

export function AnnullaDialog({ fid, onClose, onDone }) {
  const [prev, setPrev] = useState(null);
  const [elimina, setElimina] = useState(true);
  const [bozza, setBozza] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPrev(null); setElimina(true); setBozza(true);
    if (fid) api.get(`/fatture/${fid}/annulla`).then((r) => setPrev(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [fid, onClose]);
  const nElim = prev ? prev.prodotti.filter((p) => p.eliminabile).length : 0;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fatture/${fid}/annulla`, { elimina_prodotti: elimina, conserva_bozza: bozza });
      toast.success(`Caricamento annullato${data.prodotti_eliminati ? ` · ${data.prodotti_eliminati} prodotti eliminati` : ""}${bozza ? " · fattura tornata in bozza" : " · fattura eliminata"}`);
      onDone(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!fid} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="annulla-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 className="h-5 w-5 text-[#8C6D62]" />Annulla caricamento</DialogTitle>
          <DialogDescription>{prev ? <>Fattura <b>{prev.fattura.numero || "s.n."}</b> di {prev.fattura.fornitore_nome} del {fmtDate(prev.fattura.data)}: tutto torna esattamente allo stato precedente al caricamento.</> : "Calcolo dell'anteprima…"}</DialogDescription>
        </DialogHeader>
        {!prev ? <Loading /> : (
          <div className="space-y-4 text-sm">
            {prev.prodotti.length > 0 && (
              <table className="w-full text-xs" data-testid="annulla-prodotti">
                <thead className="text-[10px] uppercase tracking-wider text-[#756B64]"><tr><th className="text-left py-1">Prodotto</th><th className="text-right">Giacenza</th><th className="text-right">Costo medio</th></tr></thead>
                <tbody className="divide-y divide-[#EAE3DC]">
                  {prev.prodotti.map((p, i) => (
                    <tr key={p.prodotto_id} data-testid={`annulla-prodotto-${i}`}>
                      <td className="py-1.5">{p.nome}{p.eliminabile && <span className="ml-1 text-[10px] text-[#A2513C]">(creato per questa fattura)</span>}</td>
                      <td className="text-right tabular whitespace-nowrap">{fmtQty(p.giacenza_prima)} <ArrowRight className="inline h-3 w-3" /> <b>{fmtQty(p.giacenza_dopo)}</b></td>
                      <td className="text-right tabular whitespace-nowrap"><Cost v={p.costo_medio_prima} /> <ArrowRight className="inline h-3 w-3" /> <b><Cost v={p.costo_medio_dopo} /></b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="rounded-xl bg-[#FAF7F5] p-3" data-testid="annulla-movimenti">
              <div className="font-semibold mb-1">Movimenti di costo rimossi · {fmtEUR(prev.totale_movimenti)}</div>
              <ul className="text-xs text-[#5E4A42] space-y-0.5">{prev.movimenti.map((m, i) => <li key={i} className="flex"><span className="truncate">{m.categoria} — {m.descrizione}</span><span className="ml-auto tabular">{fmtEUR(m.imponibile)}</span></li>)}</ul>
              {prev.mappature.length > 0 && <div className="text-xs text-[#756B64] mt-2">Verranno rimossi {prev.mappature.length} abbinamenti appresi da questa fattura.</div>}
            </div>
            {prev.collegati.length > 0 && <div className="text-xs text-[#A2513C]" data-testid="annulla-collegati">Documenti accessori collegati: {prev.collegati.map((c) => c.numero || "s.n.").join(", ")} — i loro costi non saranno più ripartiti finché la fattura non viene riconfermata.</div>}
            <label className="flex items-start gap-2 cursor-pointer"><input type="checkbox" data-testid="annulla-elimina-prodotti" checked={elimina} onChange={(e) => setElimina(e.target.checked)} className="mt-0.5 accent-[#8C6D62]" />Elimina anche i prodotti creati da questa fattura, se non usati altrove ({nElim})</label>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="annulla-modo" data-testid="annulla-conserva-bozza" checked={bozza} onChange={() => setBozza(true)} className="accent-[#8C6D62]" />Conserva fattura e file come <b>bozza</b> (potrai correggerla e riconfermarla)</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="annulla-modo" data-testid="annulla-elimina-fattura" checked={!bozza} onChange={() => setBozza(false)} className="accent-[#8C6D62]" />Elimina fattura e file</label>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className={btnGhost} onClick={onClose} disabled={busy} data-testid="annulla-chiudi">Chiudi</Button>
          <Button className={btnPrimary} onClick={go} disabled={!prev || busy} data-testid="annulla-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Annulla caricamento"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
